import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle, ArrowRight } from 'lucide-react';
import { getRef } from '../lib/referral';
import Logo from './Logo';
import OSTicker from './OSTicker';
import { useT } from '../i18n';
import { TEAM_AVATARS } from './teamAvatars';
import TextSizeControl from './TextSizeControl';

export function LinkedInIcon({ className = '', mono = false }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill={mono ? 'currentColor' : '#0A66C2'} d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.61 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

export function FacebookIcon({ className = '', mono = false }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill={mono ? 'currentColor' : '#1877F2'} d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.92v-8.43H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.09 24 18.09 24 12.07z" />
    </svg>
  );
}

export function XIcon({ className = '', mono = true }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="currentColor" d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  );
}

export function InstagramIcon({ className = '', mono = false }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <radialGradient id="ig-grad-footer" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <path
        fill={mono ? 'currentColor' : 'url(#ig-grad-footer)'}
        d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.73 1.46 1.38 2.13.67.65 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84Zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4Zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44Z"
      />
    </svg>
  );
}

const socials = [
  { Icon: LinkedInIcon, href: 'https://www.linkedin.com/company/kitxenscom', label: 'LinkedIn' },
  { Icon: FacebookIcon, href: 'https://www.facebook.com/kitxenscom', label: 'Facebook' },
  { Icon: InstagramIcon, href: 'https://www.instagram.com/kitxenscom', label: 'Instagram' },
  { Icon: XIcon, href: 'https://x.com/kitxensdotcom', label: 'X' },
];

export default function Footer() {
  const { lang } = useT();
  const isEs = lang === 'es';

  // Footer mirrors the top nav structure: Services / AI Solutions / Company / Legal
  const services = {
    title: isEs ? 'Servicios' : 'Services',
    links: [
      { label: isEs ? 'Tecnología para Restaurantes' : 'Restaurant Technology', href: '/it-pos-systems' },
      { label: isEs ? 'Presencia Digital' : 'Digital Presence', href: '/digital-branding' },
      { label: isEs ? 'Crecimiento del Restaurante' : 'Restaurant Growth', href: '/restaurant-revenue-growth' },
      { label: isEs ? 'Inteligencia del Restaurante' : 'Restaurant Intelligence', href: '/services/data-analytics' },
    ],
  };

  const aiSolutions = {
    title: isEs ? 'Soluciones IA' : 'AI Solutions',
    links: [
      { label: 'Index AI', href: '/index-ai' },
      { label: isEs ? 'Agentes de IA' : 'AI Agents', href: '/ai-agents-for-restaurants' },
      { label: isEs ? 'Automatización IA Personalizada' : 'Custom AI Automation', href: '/ai/custom-ai-automation' },
      { label: isEs ? 'Recepcionista IA' : 'AI Receptionist', href: '/ai/ai-receptionist' },
      { label: isEs ? 'Automatización de Reseñas' : 'Review Automation', href: '/ai/review-automation' },
      { label: 'Catering AI', href: '/ai/catering-ai' },
    ],
  };

  const company = {
    title: isEs ? 'Empresa' : 'Company',
    links: [
      { label: isEs ? 'Acerca de' : 'About', href: '/aboutus' },
      { label: isEs ? 'Equipo IA' : 'AI Team', href: '/ai-team' },
      { label: isEs ? 'Cómo Funciona' : 'How It Works', href: '/how-it-works' },
      { label: isEs ? 'AI OS — En Vivo' : 'AI OS — Live', href: '/os' },
      { label: isEs ? 'Precios' : 'Pricing', href: '/pricing' },
      { label: isEs ? 'Recursos' : 'Resources', href: '/magazine' },
      { label: 'FAQ', href: '/faq' },
      { label: isEs ? 'Contacto' : 'Contact', href: '/contact' },
      { label: isEs ? 'Partners — Gana 30%' : 'Partners — Earn 30%', href: '/affiliates', highlight: true },
    ],
  };

  const legal = {
    title: isEs ? 'Legal' : 'Legal',
    links: [
      { label: isEs ? 'Política de Privacidad' : 'Privacy Policy', href: '/privacy' },
      { label: isEs ? 'Términos y Condiciones' : 'Terms & Conditions', href: '/terms' },
      { label: isEs ? 'Política de Cookies' : 'Cookie Policy', href: '/cookies' },
      { label: isEs ? 'Accesibilidad' : 'Accessibility', href: '/accessibility' },
      { label: isEs ? 'Gestionar Cookies' : 'Manage Cookies', href: '#', manageCookies: true },
    ],
  };

  const groups = [services, aiSolutions, company, legal];

  const renderLink = (l) => {
    if (l.manageCookies) {
      return (
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('kitxens:open-cookie-prefs'))}
          className="text-neutral-400 hover:text-white transition-colors text-[14.5px] text-left"
          data-testid="footer-manage-cookies"
        >
          {l.label}
        </button>
      );
    }
    if (l.external) {
      return (
        <a
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-neutral-400 hover:text-white transition-colors text-[14.5px]"
        >
          {l.label}
        </a>
      );
    }
    return (
      <Link to={l.href} className={l.highlight
        ? 'inline-flex items-center gap-1.5 text-[#FF9B4A] hover:text-[#ffb374] font-semibold transition-colors text-[14.5px]'
        : 'text-neutral-400 hover:text-white transition-colors text-[14.5px]'}
        data-testid={l.highlight ? 'footer-affiliates-link' : undefined}>
        {l.highlight && <span className="w-1.5 h-1.5 rounded-full bg-[#F77F11] animate-pulse" />}{l.label}
      </Link>
    );
  };

  return (
    <footer id="company" className="bg-[#1B1F24] text-[#B8BDC6] pt-14 lg:pt-16 pb-10" data-testid="site-footer">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <OSTicker />

        {/* ── Brand statement — final positioning line of the experience ── */}
        <Link
          to="/operating-intelligence"
          aria-label={isEs ? 'Conoce la Inteligencia Operativa de Kitxens' : 'Discover the Kitxens Operating Intelligence'}
          className="block mb-16 lg:mb-20 pb-12 lg:pb-14 border-b border-white/[0.06] group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00]/40 rounded-2xl"
          data-testid="footer-brand-statement"
        >
          <h2 className="text-[28px] sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.02em] leading-[1.08] text-white max-w-3xl">
            <span className="block group-hover:text-[#ff9b4a] transition-colors">{isEs ? 'Una Inteligencia.' : 'One Intelligence.'}</span>
            <span className="block text-neutral-400 group-hover:text-neutral-200 transition-colors">{isEs ? 'Construida para Restaurantes.' : 'Built for Restaurants.'}</span>
            <span className="block text-neutral-400 group-hover:text-neutral-200 transition-colors">{isEs ? 'Potenciada por IA.' : 'Powered by AI.'}</span>
            <span className="block bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {isEs ? 'Operada por Expertos.' : 'Operated by Experts.'}
            </span>
          </h2>
          <span className="mt-5 inline-flex items-center gap-1.5 text-[12px] tracking-[0.18em] uppercase font-bold text-neutral-400 group-hover:text-[#F77F11] transition-colors">
            {isEs ? 'Conocer la Inteligencia Operativa' : 'Discover the Operating Intelligence'}
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
          {/* Brand block */}
          <div className="md:col-span-12 lg:col-span-4">
            <Logo variant="light" />
            <div className="mt-6 max-w-md space-y-3">
              <p className="text-white font-semibold text-[15.5px] leading-snug">
                {isEs
                  ? 'Socio Tecnológico y de IA para Restaurantes.'
                  : 'Technology & AI Growth Partner for Restaurants.'}
              </p>
              <p className="text-[#F77F11] font-semibold text-sm tracking-wide">
                {isEs ? 'Conecta. Automatiza. Analiza. Crece.' : 'Connect. Automate. Analyze. Grow.'}
              </p>
              <p className="text-neutral-400 text-sm leading-relaxed">
                {isEs
                  ? 'Acompañamos a restaurantes independientes en todo el mundo.'
                  : 'Helping independent restaurants grow worldwide.'}
              </p>
            </div>

            <div className="mt-7 space-y-3 text-sm">
              <a
                href="https://wa.me/14085901102"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 text-neutral-300 hover:text-white transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                data-testid="footer-whatsapp-link"
              >
                <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#25D366] flex items-center justify-center transition-colors shrink-0">
                  <MessageCircle className="w-4 h-4 text-[#25D366] group-hover:text-white transition-colors" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="font-semibold">WhatsApp Chat</span>
                  <span className="text-[12px] tracking-[0.05em] text-neutral-400">24/7 · ESP | ENG</span>
                </span>
              </a>
              <a
                href="tel:+14155982310"
                className="group flex items-center gap-3 text-neutral-300 hover:text-white transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#F77F11] flex items-center justify-center transition-colors shrink-0">
                  <Phone className="w-4 h-4 text-[#F77F11] group-hover:text-white transition-colors" />
                </span>
                <span>+1 (415) 598-2310</span>
              </a>
              <a
                href="mailto:hello@kitxens.com"
                className="group flex items-center gap-3 text-neutral-300 hover:text-white transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span className="w-9 h-9 rounded-full bg-white/5 group-hover:bg-[#F77F11] flex items-center justify-center transition-colors shrink-0">
                  <Mail className="w-4 h-4 text-[#F77F11] group-hover:text-white transition-colors" />
                </span>
                <span>hello@kitxens.com</span>
              </a>
            </div>

            {/* Kitxens AI Team — avatars with names, below contact */}
            <div className="mt-9" data-testid="footer-team-row">
              <p className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#7D848E]">
                {isEs ? 'Equipo Operativo Kitxens' : 'Kitxens Operating Team'}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-4">
                {TEAM_AVATARS.map((m) => (
                  <Link
                    key={m.key}
                    to="/operating-intelligence"
                    className="group/avatar flex flex-col items-center gap-1.5 w-11"
                    aria-label={`${m.name} · ${m.role}`}
                    title={`${m.name} · ${m.role}`}
                    data-testid={`footer-team-avatar-${m.key}`}
                  >
                    <span className="relative inline-block">
                      <motion.span
                        whileHover={{ y: -3, scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                        className="inline-flex w-11 h-11 rounded-full overflow-hidden ring-2 ring-white/10 group-hover/avatar:ring-[#FF7A00] bg-white/5 transition-shadow duration-200"
                      >
                        <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
                      </motion.span>
                      <span className="pointer-events-none absolute bottom-0.5 right-0 flex w-2.5 h-2.5">
                        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-50" />
                        <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#1B1F24]" />
                      </span>
                    </span>
                    <span className="text-[10.5px] font-medium text-[#7D848E] group-hover/avatar:text-[#F8F6F2] transition-colors duration-200">
                      {m.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Four nav columns */}
          {groups.map((g) => (
            <div key={g.title} className="md:col-span-6 lg:col-span-2">
              <h4 className="text-white font-semibold mb-5 text-[12px] tracking-[0.16em] uppercase">
                {g.title}
              </h4>
              <ul className="space-y-3">
                {g.links.map((l) => (
                  <li key={l.label}>{renderLink(l)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── Full-width horizontal Newsletter row ── */}
        <div className="mt-16 lg:mt-20 pt-12 lg:pt-14 border-t border-white/[0.06]" data-testid="footer-newsletter">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-end">
            <div className="text-center lg:text-left">
              <h3 className="text-white text-xl sm:text-2xl font-semibold tracking-[-0.015em] leading-tight">
                {isEs ? 'Restaurant Technology Brief' : 'Restaurant Technology Brief'}
              </h3>
              <p className="mt-2 text-neutral-400 text-[14.5px] leading-relaxed max-w-xl mx-auto lg:mx-0">
                {isEs
                  ? 'Ideas mensuales para operadores de restaurantes ambiciosos.'
                  : 'Monthly insights for ambitious restaurant operators.'}
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formEl = e.currentTarget;
                const btnEl = formEl.querySelector('button');
                const inputEl = formEl.querySelector('input[name="email"]');
                const noteEl = formEl.parentElement.querySelector('[data-testid="footer-newsletter-note"]');
                const email = inputEl?.value || '';
                if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
                const original = btnEl.textContent;
                btnEl.disabled = true;
                btnEl.textContent = isEs ? 'Enviando…' : 'Sending…';
                try {
                  await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email,
                      locale: lang,
                      ref: getRef() || '',
                      source_page: typeof window !== 'undefined' ? window.location.pathname : '/footer',
                    }),
                  });
                  formEl.reset();
                  btnEl.textContent = isEs ? '¡Suscrito!' : 'Subscribed!';
                  try {
                    const { trackEvent } = await import('../lib/analytics');
                    trackEvent('newsletter_signup', { location: 'footer' });
                  } catch { /* noop */ }
                  if (noteEl) noteEl.textContent = isEs
                    ? 'Gracias por suscribirte.'
                    : 'Thanks for subscribing.';
                } catch (err) {
                  console.error('Footer newsletter submit failed', err);
                  btnEl.textContent = original;
                  btnEl.disabled = false;
                }
              }}
              className="flex flex-col sm:flex-row items-stretch gap-3 lg:min-w-[520px] w-full max-w-md mx-auto sm:max-w-none lg:w-auto"
            >
              <label htmlFor="footer-newsletter-input" className="sr-only">
                {isEs ? 'Tu correo' : 'Your email'}
              </label>
              <div className="flex-1 flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-full h-12 px-5 focus-within:border-[#F77F11]/60 focus-within:bg-white/[0.06] transition-colors">
                <Mail className="w-4 h-4 text-neutral-400 shrink-0" />
                <input
                  id="footer-newsletter-input"
                  name="email"
                  type="email"
                  required
                  placeholder={isEs ? 'tu@restaurante.com' : 'your@restaurant.com'}
                  className="flex-1 bg-transparent outline-none text-[14.5px] text-white placeholder:text-neutral-500 text-center sm:text-left"
                  data-testid="footer-newsletter-email"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[14px] font-semibold transition-colors duration-200 shrink-0"
                data-testid="footer-newsletter-submit"
              >
                {isEs ? 'Suscribirme' : 'Subscribe'} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
          <p
            className="mt-4 text-[12.5px] text-neutral-400"
            data-testid="footer-newsletter-note"
          >
            {isEs ? 'Sin spam. Cancela cuando quieras.' : 'No spam. Unsubscribe anytime.'}
          </p>
        </div>

        {/* ── Bottom bar — Stripe/Linear style: socials + site | legal | copyright + tagline ── */}
        <div className="mt-14 lg:mt-16 pt-9 border-t border-white/[0.06]">
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">

            {/* Left: social icons + website */}
            <div className="flex flex-col items-center sm:flex-row gap-4 sm:gap-5">
              <div className="flex items-center gap-1">
                {socials.map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-[#7D848E] hover:text-[#F8F6F2] hover:bg-white/[0.05] transition-colors duration-200"
                    data-testid={`footer-social-${label.toLowerCase()}`}
                  >
                    <Icon className="w-[17px] h-[17px]" mono />
                  </a>
                ))}
              </div>
              <a
                href="https://www.kitxens.com"
                className="text-[13px] font-medium tracking-wide text-[#B8BDC6] hover:text-[#FF7A00] transition-colors duration-200"
                data-testid="footer-site-link"
              >
                www.kitxens.com
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}
                target="_blank"
                rel="noopener noreferrer"
                title={isEs ? 'Feed RSS oficial de Kitxens — cópialo en tu lector o automatización' : 'Official Kitxens RSS feed — copy it into your reader or automation'}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium tracking-wide text-[#B8BDC6] hover:text-[#FF7A00] transition-colors duration-200"
                data-testid="footer-rss-link"
              >
                <svg viewBox="0 0 24 24" className="w-[15px] h-[15px]" fill="currentColor" aria-hidden="true">
                  <path d="M6.18 15.64a2.18 2.18 0 1 1 0 4.36 2.18 2.18 0 0 1 0-4.36zM4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44zm0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z" />
                </svg>
                RSS
              </a>
            </div>

            {/* Center: legal links */}
            <nav
              className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[13px]"
              aria-label={isEs ? 'Enlaces legales' : 'Legal links'}
            >
              <Link to="/privacy" className="text-[#B8BDC6] hover:text-[#F8F6F2] transition-colors duration-200">
                {isEs ? 'Privacidad' : 'Privacy'}
              </Link>
              <Link to="/terms" className="text-[#B8BDC6] hover:text-[#F8F6F2] transition-colors duration-200">
                {isEs ? 'Términos' : 'Terms'}
              </Link>
              <Link to="/cookies" className="text-[#B8BDC6] hover:text-[#F8F6F2] transition-colors duration-200">
                Cookies
              </Link>
              <Link to="/accessibility" className="text-[#B8BDC6] hover:text-[#F8F6F2] transition-colors duration-200" data-testid="footer-accessibility-link">
                {isEs ? 'Accesibilidad' : 'Accessibility'}
              </Link>
              <Link to="/contact" className="text-[#B8BDC6] hover:text-[#F8F6F2] transition-colors duration-200">
                {isEs ? 'Contacto' : 'Contact'}
              </Link>
            </nav>

            {/* Right: copyright + tagline */}
            <div className="text-center lg:text-right">
              <p className="text-[13px] font-medium text-[#B8BDC6]" data-testid="footer-copyright">
                &copy; {new Date().getFullYear()} KITXENS
              </p>
              <p className="mt-1 text-[12px] text-[#7D848E]" data-testid="footer-tagline">
                {isEs
                  ? 'Construido en Silicon Valley. Diseñado para restaurantes de todo el mundo.'
                  : 'Built in Silicon Valley. Designed for restaurants everywhere.'}
              </p>
              <p className="mt-2 text-[10px] tracking-[0.2em] text-[#4A4F57]" data-testid="footer-site-code">0Kb6E</p>
            </div>
          </div>

          {/* Accessibility statement + A-A-A control (public site keeps it here; app headers have it on top) */}
          <div className="mt-8 pt-6 border-t border-white/[0.06] flex justify-center lg:justify-start">
            <TextSizeControl isEs={isEs} />
          </div>
        </div>
      </div>
    </footer>
  );
}
